"use client";

import {
  carouselClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Video from "@/components/atoms/Video";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";

/* CU brand constants baked into the reference Testimonials design — not CMS
 * fields (the admin only controls the section title/description/colors). */
const CORAL = "#E1473F"; // primary — ring, quote mark, active arrow
const PINK = "#FFE0DE"; // primary-light-200 — quote card background
const QUOTE_TEXT = "#474343"; // gray-650
const ROLE_TEXT = "#5F5F5F"; // gray-550

export interface Faculty6MetaItem {
  id?: string | number;
  priority?: number | string;
  status?: boolean;
  metaImg?: string;
  altImg?: string;
  metaVideo?: string;
  metaTitle?: string;
  metaRole?: string;
  metaDescription?: string;
}

export interface Faculty6Data {
  id?: string;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  title_1_color?: string;
  description?: string;
  description_text_color?: string;
  background_color?: string;
  metadata?: Faculty6MetaItem[];
}

interface Faculty6Props {
  data: Faculty6Data | { data: Faculty6Data };
}

function resolvePayload(data: Faculty6Props["data"]): Faculty6Data | undefined {
  const anyData = data as { data?: Faculty6Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Faculty6Data;
  }
  return data as Faculty6Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** `{highlight}` / `{{line}}` → span with admin-chosen highlight color (inline
 *  style so arbitrary hex values bypass Tailwind JIT). A literal newline in the
 *  admin textarea breaks the line there — `{{ }}` can only break *after* the
 *  highlighted words, so a title whose second line starts with the highlight
 *  (e.g. "Success Stories - Hear from\n{Our Students}") needs the newline. */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  const formatLine = (line: string): string => {
    if (line.includes("{{")) {
      return line
        .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
        .replace(/\s+/g, " ");
    }

    if (line.includes("{")) {
      return line
        .replace(/\{/g, openTag)
        .replace(/\}/g, "</span>")
        .replace(/\s+/g, " ");
    }

    return escapeHtml(line);
  };

  return title
    .trim()
    .split(/\r\n|\r|\n/)
    .map((line) => formatLine(line.trim()))
    .join("<br/>");
}

/* Decorative coral arc that peeks out at the top-left of the avatar — the CU
 * "Ellipse" flourish. */
const EllipseArc = ({ className = "" }: { className?: string }) => (
  <svg
    width={94}
    height={84}
    viewBox="0 0 95 85"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M12.0786 68.5864C12.9309 50.4931 20.6663 33.4097 33.7015 20.8327C46.7366 8.25573 64.0858 1.13637 82.1981 0.931775L82.3218 11.883C67.0034 12.056 52.3303 18.0772 41.3059 28.7141C30.2814 39.3511 23.7393 53.7994 23.0184 69.1018L12.0786 68.5864Z"
      fill={CORAL}
    />
  </svg>
);

/* CU double quote mark. */
const QuoteMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path
      d="M15.984 19.3532C15.2933 19.3532 14.3807 18.4652 13.246 16.6892C12.1113 14.9132 11.544 13.3346 11.544 11.9532C11.544 10.5226 12.4567 8.32725 14.282 5.36725C16.1567 2.40725 17.4393 0.927246 18.13 0.927246C18.2287 0.927246 18.426 1.05058 18.722 1.29725C19.018 1.54391 19.166 1.76591 19.166 1.96325C19.166 2.11125 18.6973 2.90058 17.76 4.33125C16.8227 5.71258 16.354 6.97058 16.354 8.10525C16.354 9.23991 16.872 10.5719 17.908 12.1012C18.9933 13.6306 19.536 14.5432 19.536 14.8392C19.536 15.1352 19.3387 15.6286 18.944 16.3192C17.7107 18.3419 16.724 19.3532 15.984 19.3532ZM4.44 19.3532C3.74933 19.3532 2.83667 18.4652 1.702 16.6892C0.567333 14.9132 0 13.3346 0 11.9532C0 10.5226 0.912667 8.32725 2.738 5.36725C4.61267 2.40725 5.89533 0.927246 6.586 0.927246C6.68467 0.927246 6.882 1.05058 7.178 1.29725C7.474 1.54391 7.622 1.76591 7.622 1.96325C7.622 2.11125 7.15333 2.90058 6.216 4.33125C5.27867 5.71258 4.81 6.97058 4.81 8.10525C4.81 9.23991 5.328 10.5719 6.364 12.1012C7.44933 13.6306 7.992 14.5432 7.992 14.8392C7.992 15.1352 7.79467 15.6286 7.4 16.3192C6.16667 18.3419 5.18 19.3532 4.44 19.3532Z"
      fill={CORAL}
    />
  </svg>
);

/* Circular play glyph shown on cards that have a video. */
const PlayGlyph = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M25.9785 45.5522C28.49 45.5522 30.977 45.0576 33.2973 44.0964C35.6177 43.1353 37.726 41.7266 39.5019 39.9507C41.2779 38.1747 42.6866 36.0664 43.6477 33.7461C44.6088 31.4257 45.1035 28.9388 45.1035 26.4272C45.1035 23.9157 44.6088 21.4288 43.6477 19.1084C42.6866 16.7881 41.2779 14.6797 39.5019 12.9038C37.726 11.1279 35.6177 9.71917 33.2973 8.75805C30.977 7.79693 28.49 7.30225 25.9785 7.30225C20.9062 7.30225 16.0417 9.3172 12.4551 12.9038C8.86847 16.4905 6.85352 21.355 6.85352 26.4272C6.85352 31.4995 8.86847 36.364 12.4551 39.9507C16.0417 43.5373 20.9062 45.5522 25.9785 45.5522ZM23.3924 17.906L35.3859 24.57C35.717 24.7541 35.9929 25.0233 36.1849 25.3499C36.377 25.6764 36.4783 26.0484 36.4783 26.4272C36.4783 26.8061 36.377 27.178 36.1849 27.5046C35.9929 27.8311 35.717 28.1004 35.3859 28.2845L23.3924 34.9485C23.004 35.1644 22.566 35.275 22.1218 35.2695C21.6775 35.2639 21.2424 35.1424 20.8595 34.917C20.4767 34.6915 20.1594 34.3699 19.9391 33.9841C19.7188 33.5983 19.6031 33.1616 19.6035 32.7172V20.1372C19.6031 19.6929 19.7188 19.2562 19.9391 18.8704C20.1594 18.4846 20.4767 18.163 20.8595 17.9375C21.2424 17.7121 21.6775 17.5906 22.1218 17.585C22.566 17.5795 23.004 17.6901 23.3924 17.906Z"
      fill="white"
    />
  </svg>
);

const Faculty6 = ({ data }: Faculty6Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeVideo, setActiveVideo] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const {
    status = true,
    title = "",
    title_text_color = "#1A1A1A",
    title_1_color = CORAL,
    description = "",
    description_text_color = "#1A1A1A",
    background_color = "#FFFFFF",
    metadata = [],
  } = sectionData || {};

  const items = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter((m) => m && (m.status === undefined || m.status === true))
      .sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0));
  }, [metadata]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status && items.length > 0) {
  //     FireWidgetViewedEvent(ref, "faculty_6");
  //   }
  // }, [status, items.length]);

  // Keep currentIndex in range if the items list shrinks.
  useEffect(() => {
    if (currentIndex > items.length - 1) {
      setCurrentIndex(Math.max(0, items.length - 1));
    }
  }, [items.length, currentIndex]);

  if (!status || items.length === 0) return null;

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex >= items.length - 1;
  const multiple = items.length > 1;

  // Each slide takes 70% of the viewport on desktop (so the next card peeks in
  // on the right) and a full width on mobile. The active slide is left-aligned
  // to the track start; the carousel reserves horizontal padding for the arrows.
  const basis = isMobile ? 100 : 70;
  const offsetPct = currentIndex * basis;

  const handlePrev = () => {
    if (isAtStart) return;
    setCurrentIndex((i) => i - 1);
    carouselClick({ section: "faculty_6", label: "left" });
  };
  const handleNext = () => {
    if (isAtEnd) return;
    setCurrentIndex((i) => i + 1);
    carouselClick({ section: "faculty_6", label: "right" });
  };

  return (
    <section
      ref={ref}
      data-faculty="6"
      className="flex w-full items-center justify-center"
      style={{ backgroundColor: background_color }}
    >
      <div className="relative mx-auto flex w-full max-w-screen-container flex-col items-center gap-10 px-4 py-10 min-[768px]:gap-12 min-[768px]:py-14">
        {/* Title + Description */}
        <div className="flex w-[300px] flex-col items-center justify-center gap-3.5 text-center min-[768px]:w-[766px] min-[768px]:gap-1.5">
          {title ? (
            <h2
              className="font-poppins text-[28px] font-semibold leading-tight min-[768px]:text-[32px]"
              style={{ color: title_text_color }}
              dangerouslySetInnerHTML={{
                __html: formatTitleWithHighlight(title, title_1_color),
              }}
            />
          ) : null}
          {description ? (
            <p
              className="text-[14px] min-[768px]:text-[18px]"
              style={{ color: description_text_color }}
            >
              {description}
            </p>
          ) : null}
        </div>

        {/* Testimonial carousel — peek of the next card, coral side arrows */}
        <div className="relative w-full">
          <div className="overflow-hidden px-2 min-[768px]:px-24">
            <div
              className={cn(
                "flex transition-transform duration-500 ease-out",
                // A lone card has nothing to slide to, so center it instead of
                // leaving it pinned to the left with empty space on the right.
                !multiple && "justify-center",
              )}
              style={{ transform: `translateX(-${offsetPct}%)` }}
            >
              {items.map((item, idx) => (
                <div
                  key={item.id ?? `card-${idx}`}
                  className="shrink-0 px-2 min-[768px]:px-3"
                  style={{ flex: `0 0 ${basis}%` }}
                >
                  <Faculty6Card
                    item={item}
                    onPlay={() =>
                      item.metaVideo && setActiveVideo(item.metaVideo)
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {multiple ? (
            <>
              {/* Desktop: coral arrows overlaying the sides of the track */}
              <CarouselArrow
                direction="prev"
                disabled={isAtStart}
                onClick={handlePrev}
                className="absolute left-2 top-1/2 hidden -translate-y-1/2 min-[768px]:flex"
              />
              <CarouselArrow
                direction="next"
                disabled={isAtEnd}
                onClick={handleNext}
                className="absolute right-2 top-1/2 hidden -translate-y-1/2 min-[768px]:flex"
              />

              {/* Mobile: arrows sit below the card, centered */}
              <div className="mt-6 flex items-center justify-center gap-5 min-[768px]:hidden">
                <CarouselArrow
                  direction="prev"
                  disabled={isAtStart}
                  onClick={handlePrev}
                />
                <CarouselArrow
                  direction="next"
                  disabled={isAtEnd}
                  onClick={handleNext}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>

      {activeVideo ? (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo("")} />
      ) : null}
    </section>
  );
};

interface CarouselArrowProps {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
  className?: string;
}

const CarouselArrow = ({
  direction,
  disabled,
  onClick,
  className,
}: CarouselArrowProps) => {
  const isPrev = direction === "prev";
  const Icon = isPrev ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={isPrev ? "Previous testimonial" : "Next testimonial"}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "z-10 flex size-[45px] shrink-0 items-center justify-center rounded-full shadow-[0px_4px_4px_0px_#00000040] transition-colors min-[768px]:size-[63px]",
        className,
      )}
      style={
        disabled
          ? { backgroundColor: PINK, color: CORAL }
          : { backgroundColor: CORAL, color: "#FFFFFF" }
      }
    >
      <Icon className="size-6 min-[768px]:size-7" strokeWidth={2.5} />
    </button>
  );
};

interface Faculty6CardProps {
  item: Faculty6MetaItem;
  onPlay: () => void;
}

/** CU testimonial card: decorative coral ring + bordered avatar overlapping the
 *  top-left of a light-pink quote box (quote mark, quote text, name, role). */
const Faculty6Card = ({ item, onPlay }: Faculty6CardProps) => {
  const {
    metaImg = "",
    altImg = "",
    metaVideo = "",
    metaTitle = "",
    metaRole = "",
    metaDescription = "",
  } = item;

  return (
    <div className="flex h-full w-full justify-center min-[768px]:justify-start">
      <div className="w-full">
        {/* Avatar with decorative ring + optional play button */}
        <div
          className={cn(
            "relative z-10 top-2 ml-[18%] inline-block h-[128px] min-[768px]:top-16 min-[768px]:ml-0",
            metaVideo && "cursor-pointer",
          )}
          onClick={() => metaVideo && onPlay()}
        >
          <EllipseArc />
          <div
            className="absolute left-5 top-[7px] flex size-[128px] items-center justify-center overflow-hidden rounded-full border-[10px] min-[768px]:size-[142px]"
            style={{ borderColor: CORAL }}
          >
            <div className="absolute inset-0 z-10 bg-black/20" />
            {metaImg ? (
              <Image
                src={metaImg}
                alt={altImg || metaTitle || "testimonial"}
                fill
                className="object-cover"
                sizes="142px"
              />
            ) : null}
            {metaVideo ? (
              <PlayGlyph className="absolute z-20 size-[52px]" />
            ) : null}
          </div>
        </div>

        {/* Quote box */}
        <div
          className="relative flex w-fit gap-2 px-5 pb-7 pt-4 min-[768px]:left-20 min-[768px]:gap-1 min-[768px]:pb-8 min-[768px]:pl-[92px] min-[768px]:pr-14 min-[768px]:pt-7"
          style={{ backgroundColor: PINK }}
        >
          <QuoteMark className="mt-1 size-[18px] shrink-0 min-[768px]:size-5" />
          <div className="mt-2.5 flex w-[238px] flex-col gap-4 min-[768px]:w-[350px]">
            <p
              className="min-h-[96px] text-xs font-normal min-[768px]:text-[14px]"
              style={{ color: QUOTE_TEXT }}
            >
              {metaDescription}
            </p>
            <div className="flex flex-col gap-2">
              {metaTitle ? (
                <p className="text-base font-bold min-[768px]:text-[18px]">{metaTitle}</p>
              ) : null}
              {metaRole ? (
                <p
                  className="pb-3 text-sm font-medium min-[768px]:pb-0 min-[768px]:text-base"
                  style={{ color: ROLE_TEXT }}
                >
                  {metaRole}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface VideoModalProps {
  video: string;
  onClose: () => void;
}

/** Lightweight self-contained video modal — the `Video` atom auto-detects
 *  YouTube vs direct file sources. */
const VideoModal = ({ video, onClose }: VideoModalProps) => {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="relative aspect-video w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close video"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-10 flex size-9 items-center justify-center rounded-full border border-white bg-black text-white min-[640px]:-right-3 min-[640px]:-top-3"
        >
          <X className="size-[18px]" strokeWidth={1.75} />
        </button>
        <Video
          src={video}
          controls
          autoPlay
          className="size-full rounded-lg object-contain"
        />
      </div>
    </div>
  );
};

export default Faculty6;
